import { useState, useRef, useEffect } from 'react';
import { HARVEST_PHASES, HARVEST_PHASE_LABELS, type HarvestPhase } from '../../../constants';
import styles from './PhaseSelect.module.css';

interface PhaseSelectProps {
	value: HarvestPhase | '';
	onChange: (value: HarvestPhase | '') => void;
	disabled?: boolean;
}

export const PhaseSelect = ({ value, onChange, disabled }: PhaseSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const displayText = value ? HARVEST_PHASE_LABELS[value] : 'Selecione';

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const handleToggle = () => {
		if (!disabled) setIsOpen((prev) => !prev);
	};

	const handleSelect = (phase: HarvestPhase) => {
		onChange(phase);
		setIsOpen(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (disabled) return;

		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			setIsOpen((prev) => !prev);
		} else if (e.key === 'Escape') {
			setIsOpen(false);
		}
	};

	return (
		<div className={styles.container} ref={containerRef}>
			<div
				className={`${styles.trigger} ${disabled ? styles.disabled : ''} ${isOpen ? styles.open : ''} ${!value ? styles.placeholder : ''}`}
				onClick={handleToggle}
				onKeyDown={handleKeyDown}
				tabIndex={disabled ? -1 : 0}
				role="combobox"
				aria-expanded={isOpen}
				aria-haspopup="listbox"
			>
				<span className={styles.text}>{displayText}</span>
				<svg
					className={styles.arrow}
					width="8"
					height="5"
					viewBox="0 0 12 8"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M1 1.5L6 6.5L11 1.5"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</div>

			{isOpen && (
				<div className={styles.dropdown} role="listbox">
					{HARVEST_PHASES.map((phase) => (
						<div
							key={phase}
							className={`${styles.option} ${phase === value ? styles.optionSelected : ''}`}
							onClick={() => handleSelect(phase)}
							role="option"
							aria-selected={phase === value}
						>
							{HARVEST_PHASE_LABELS[phase]}
						</div>
					))}
				</div>
			)}
		</div>
	);
};
